import { DuctedFuelToken } from 'utils/kept-system-type-annotation';
import { splitWantLabel, wantDotColor } from 'utils/recommendation-tiles-logic';
import {
  recommendationSystemCount,
  SystemCountsByCode,
  systemCountLabel
} from 'utils/recommendation-system-counts';
import { ScenarioRecommendation } from 'utils/recommendations-strip-logic';

type RecommendationsListProps = {
  // The server's flat deduped view (one row per systemTypeId, rank =
  // first-occurrence order across wants).
  recommendations: ScenarioRecommendation[];
  // systemTypeId → wantIds that recommended it (from the per-want groups,
  // which stay in the payload for exactly this attribution).
  attribution: Map<number, number[]>;
  wantIds: number[];
  wantNameOf: (id: number) => string;
  mergedAcrossAgeBuckets: boolean;
  systemCountsByCode?: SystemCountsByCode | null;
  fuelNarrow?: DuctedFuelToken[];
};

// The restyled ranked recommendations (AHA-6681 §J): a single deduped list
// with per-row attribution dots keyed (by color) to the want tiles, a dot
// legend, an ⓘ tooltip carrying the age-ranges footnote, and the
// right-aligned "Not searchable" pill. The headed per-want sections are
// gone from the UI — the payload is untouched.
export default function RecommendationsList({
  recommendations,
  attribution,
  wantIds,
  wantNameOf,
  mergedAcrossAgeBuckets,
  systemCountsByCode,
  fuelNarrow = []
}: RecommendationsListProps) {
  if (recommendations.length === 0) return null;

  // Legend entries skip ids whose name doesn't resolve (transient reconcile
  // window) — same rule as the dropdown mode's chips.
  const legendWants = wantIds
    .map((id) => ({ id, name: wantNameOf(id) }))
    .filter((want) => want.name !== '');

  const renderDots = (systemTypeId: number) =>
    (attribution.get(systemTypeId) ?? [])
      .filter((wantId) => wantIds.includes(wantId))
      .map((wantId) => (
        <span
          key={wantId}
          data-testid='rec-attribution-dot'
          title={wantNameOf(wantId)}
          className={`inline-block h-2 w-2 rounded-full ${wantDotColor(
            wantIds,
            wantId
          )}`}
        />
      ));

  return (
    <div className='mb-3'>
      <div className='flex flex-wrap items-center gap-2 text-xs font-semibold text-gray-700'>
        Recommended for you
        {mergedAcrossAgeBuckets && (
          <span
            data-testid='rec-age-info'
            title='Some recommendations cover both age ranges.'
            className='inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-400 font-serif text-[9px] italic text-gray-400'
          >
            i
          </span>
        )}
        {legendWants.length > 0 &&
          legendWants.map((want) => (
            <span
              key={want.id}
              className='inline-flex items-center gap-1 text-[10.5px] font-normal text-gray-500'
            >
              <span
                className={`inline-block h-2 w-2 rounded-full ${wantDotColor(
                  wantIds,
                  want.id
                )}`}
              />
              {splitWantLabel(want.name).line1}
            </span>
          ))}
      </div>
      <ol className='mt-1.5'>
        {recommendations.map((rec, index) => {
          const systemCount = recommendationSystemCount(
            rec.compatibilityCodes,
            systemCountsByCode,
            fuelNarrow
          );
          return (
            <li
              key={rec.systemTypeId}
              className='mb-1.5 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm last:mb-0'
            >
              <span className='inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-gray-200 text-[11px] font-bold text-gray-600'>
                {index + 1}
              </span>
              <span className='font-medium text-gray-900'>{rec.name}</span>
              {systemCount !== null && (
                <span
                  data-testid='rec-system-count'
                  className='text-xs text-gray-500'
                >
                  ({systemCountLabel(systemCount)})
                </span>
              )}
              {renderDots(rec.systemTypeId)}
              {rec.compatibilityCodes.length === 0 && (
                <span className='ml-auto rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
                  Not searchable
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
